import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Col, Row, CardBody, Card } from 'reactstrap';
import { Tooltip, IconButton, FormControl, InputLabel, Select, MenuItem, Box } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import HubIcon from '@mui/icons-material/OtherHouses';
import BadgeIcon from '@mui/icons-material/Badge';
import SettingsIcon from '@mui/icons-material/Settings';
import TimeToLeaveIcon from '@mui/icons-material/TimeToLeave';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

import RadioSelectTable from 'src/shared/components/table/RadioSelectTable';
import { getSubscriptionStatus } from 'src/utils/validationUtils';
import { format } from 'date-fns';
import PndRestApi from 'src/utils/pnd/PndRestApi';

const VEHICLE_COLUMNS = [
  {
    key: 'plateNo',
    header: 'Plate No.',
    sortable: true,
  },
  {
    key: 'vehicleUniqueId',
    header: 'Vehicle ID',
    sortable: true,
  },
  {
    key: 'modelName',
    header: 'Model',
    sortable: true,
  },
{
    key: 'hubName',
    header: 'Hub',
    sortable: true,
  },

];

const VehicleDetails = ({
  reactTableData,
  isNoRecordFound,
  onAdd,
  onEdit,
}) => {
  const rows = reactTableData?.tableRowsData ?? [];

  const [selectedVehicle, setSelectedVehicle] = useState(
    rows.length > 0 ? rows[0] : null
  );
  const [selectedHub, setSelectedHub] = useState('all');
  const [hubAssignment, setHubAssignment] = useState('all');
  const [driverAssignment, setDriverAssignment] = useState('all');
  const [hubsList, setHubsList] = useState([]);

  useEffect(() => {
    const fetchHubs = async () => {
      try {
        const response = await PndRestApi.GetAllActiveHub();
        if (response.status === 200 && response.data.status === 200) {
          setHubsList(response.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching hubs:', error);
      }
    };
    fetchHubs();
  }, []);

  const filteredRows = rows.filter(vehicle => {
    // Hub Assignment Filter
    if (hubAssignment === 'assigned' && !vehicle.hubName) return false;
    if (hubAssignment === 'unassigned' && vehicle.hubName) return false;
    
    // Driver Assignment Filter
    const isDriverAssigned = !!(
      vehicle.driverId || 
      vehicle.driverName || 
      vehicle.riderId || 
      vehicle.riderName || 
      vehicle.driver || 
      vehicle.rider || 
      vehicle.isAssigned
    );
    if (driverAssignment === 'assigned' && !isDriverAssigned) return false;
    if (driverAssignment === 'unassigned' && isDriverAssigned) return false;

    // Specific Hub Filter
    if (selectedHub !== 'all' && vehicle.hubName !== selectedHub) return false;
    
    return true;
  });

  useEffect(() => {
    if (filteredRows.length > 0) {
      if (!selectedVehicle || !filteredRows.some(v => v.vehicleId === selectedVehicle.vehicleId)) {
        setSelectedVehicle(filteredRows[0]);
      }
    } else {
      setSelectedVehicle(null);
    }
  }, [selectedHub, hubAssignment, driverAssignment, rows]);

  const handleHubAssignmentChange = (val) => {
    setHubAssignment(val);
    if (val === 'unassigned') {
      setSelectedHub('all');
    }
  };

  const getExpiryBadge = (dateValue) => {
    if (!dateValue) return <span className="badge bg-secondary">N/A</span>;
    try {
      const formattedDate = format(new Date(dateValue), 'yyyy-MM-dd');
      const status = getSubscriptionStatus(formattedDate);
      
      let bg = '#10b981'; // green
      let text = '#ffffff';
      let label = 'Valid';
      
      if (status === 'expired') {
        bg = '#ef4444'; // red
        label = 'Expired';
      } else if (status === 'expiring-this-month') {
        bg = '#eab308'; // yellow
        label = 'Expiring This Month';
      } else if (status === 'expiring-next-month') {
        bg = '#f97316'; // orange
        label = 'Expiring Next Month';
      } else {
        label = 'Active';
      }
      
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <span className="fw-semibold text-dark">{formattedDate}</span>
          <span style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '0.7rem',
            fontWeight: 700,
            backgroundColor: bg,
            color: text,
            width: 'fit-content'
          }}>
            {label.toUpperCase()}
          </span>
        </div>
      );
    } catch (e) {
      return <span className="badge bg-secondary">INVALID DATE</span>;
    }
  };

  return (
    <Col md={12} lg={12} style={{ marginTop: '20px' }}>
      <Row>
        {/* ── Left: Master List ───────────────────────────── */}
        <Col md={7} lg={7}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="hub-assignment-filter-label">Hub Assignment</InputLabel>
              <Select
                labelId="hub-assignment-filter-label"
                value={hubAssignment}
                label="Hub Assignment"
                onChange={(e) => handleHubAssignmentChange(e.target.value)}
                sx={{ borderRadius: 2, bgcolor: 'white' }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="assigned">Hub Assigned</MenuItem>
                <MenuItem value="unassigned">Hub Unassigned</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="driver-assignment-filter-label">Driver Assignment</InputLabel>
              <Select
                labelId="driver-assignment-filter-label"
                value={driverAssignment}
                label="Driver Assignment"
                onChange={(e) => setDriverAssignment(e.target.value)}
                sx={{ borderRadius: 2, bgcolor: 'white' }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="assigned">Driver Assigned</MenuItem>
                <MenuItem value="unassigned">Driver Unassigned</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="hub-filter-label">Filter by Hub</InputLabel>
              <Select
                labelId="hub-filter-label"
                value={selectedHub}
                label="Filter by Hub"
                onChange={(e) => setSelectedHub(e.target.value)}
                sx={{ borderRadius: 2, bgcolor: 'white' }}
                disabled={hubAssignment === 'unassigned'}
              >
                <MenuItem value="all">All Hubs</MenuItem>
                {hubsList.map(hub => (
                  <MenuItem key={hub.hubId} value={hub.hubName}>{hub.hubName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <RadioSelectTable
            rows={filteredRows}
            columns={VEHICLE_COLUMNS}
            rowKey="vehicleId"
            selectedKey={selectedVehicle?.vehicleId}
            onSelect={setSelectedVehicle}
            searchKeys={['plateNo', 'vehicleUniqueId', 'modelName', 'makerName', 'hubName']}
            searchPlaceholder="Search by Plate, ID, Model, Make..."
            sortKey="plateNo"
            headerTitle="Vehicle List"
            onAdd={onAdd}
            pageSizes={[5, 10, 20, 50]}
          />
        </Col>

        {/* ── Right: Detail Panel ─────────────────────────── */}
        <Col md={5} lg={5}>
          <Card style={{
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
            minHeight: '520px',
            background: '#ffffff',
            overflow: 'hidden',
          }}>
            {selectedVehicle ? (
              <>
                {/* Header Banner */}
                <div style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  padding: '24px 20px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <DirectionsCarIcon style={{ color: '#ffffff', fontSize: '2.5rem', opacity: 0.9 }} />
                      <div>
                        <h4 style={{ margin: 0, fontWeight: 800, fontSize: '1.2rem', color: '#ffffff' }}>
                          Plate: {selectedVehicle.plateNo}
                        </h4>
                        <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)' }}>
                          ID: {selectedVehicle.vehicleUniqueId}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <span
                        className={`badge ${selectedVehicle.status ? 'bg-success' : 'bg-danger'}`}
                        style={{ fontSize: '0.75rem', fontWeight: 700 }}
                      >
                        {selectedVehicle.status ? 'ACTIVE' : 'IN-ACTIVE'}
                      </span>
                      
                      <Tooltip title="Edit details">
                        <IconButton
                          size="small"
                          onClick={() => onEdit(selectedVehicle)}
                          style={{ color: '#ffffff', backgroundColor: 'rgba(255,255,255,0.15)' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                </div>

                {/* Details Body */}
                <CardBody style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                    {/* Vehicle Specifications */}
                    <div>
                      <h6 className="text-muted text-uppercase fw-bold mb-2"
                        style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        Vehicle Details
                      </h6>
                      <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Make / Manufacturer:</span>
                          <span className="fw-bold small text-dark d-flex align-items-center gap-1">
                            <TimeToLeaveIcon style={{ fontSize: '14px', color: '#64748b' }} />
                            {selectedVehicle.makerName || '—'}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Model Name:</span>
                          <span className="fw-bold small text-dark d-flex align-items-center gap-1">
                            <SettingsIcon style={{ fontSize: '14px', color: '#64748b' }} />
                            {selectedVehicle.modelName || '—'}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Vehicle Type:</span>
                          <span className="fw-bold small text-dark d-flex align-items-center gap-1">
                            <BadgeIcon style={{ fontSize: '14px', color: '#64748b' }} />
                            {selectedVehicle.vehicleType || '—'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Registration Expiry Status */}
                    <div>
                      <h6 className="text-muted text-uppercase fw-bold mb-2"
                        style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        Registration Status
                      </h6>
                      <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-muted small d-flex align-items-center gap-1">
                            <CalendarTodayIcon style={{ fontSize: '14px', color: '#64748b' }} />
                            Registration Expiry:
                          </span>
                          {getExpiryBadge(selectedVehicle.vehicleRegistrationExpiryDate)}
                        </div>
                      </div>
                    </div>

                    {/* Hub Assignment */}
                    <div>
                      <h6 className="text-muted text-uppercase fw-bold mb-2"
                        style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        Hub Allocations
                      </h6>
                      <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-muted small">Assigned Hub:</span>
                          {selectedVehicle.hubName ? (
                            <span className="badge bg-primary text-wrap" style={{ fontSize: '0.78rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 8px' }}>
                              <HubIcon style={{ fontSize: '14px' }} />
                              {selectedVehicle.hubName}
                            </span>
                          ) : (
                            <span className="badge bg-secondary" style={{ fontSize: '0.75rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 8px' }}>
                              <HubIcon style={{ fontSize: '14px' }} />
                              Unassigned
                            </span>
                          )}
                        </div>
                   
                    </div>
                  </div>

                  {/* Information Note */}
                  <div style={{
                    background: '#f0f9ff',
                    border: '1px solid #bae6fd',
                    borderRadius: '8px',
                    padding: '10px 12px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                    marginTop: '8px'
                  }}>
                    <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>ℹ️</span>
                    <span style={{ fontSize: '0.78rem', color: '#0369a1', lineHeight: '1.4', fontWeight: 500 }}>
                      Note: If a new driver is created and you want to assign a vehicle, please go to the <strong>Driver Vehicle Mapping</strong> menu.
                    </span>
                  </div>

                </div>
              </CardBody>
              </>
            ) : (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', padding: '40px', textAlign: 'center', minHeight: '520px',
              }}>
                <div style={{ fontSize: '3.5rem', color: '#cbd5e1', marginBottom: '16px' }}>🚗</div>
                <h5 className="fw-bold text-muted">No Vehicle Selected</h5>
                <p className="text-muted small">Select a vehicle from the list to view details.</p>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </Col>
  );
};

VehicleDetails.propTypes = {
  reactTableData: PropTypes.shape({
    tableRowsData: PropTypes.arrayOf(PropTypes.shape()),
  }),
  isNoRecordFound: PropTypes.bool,
  onAdd: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onAssignHub: PropTypes.func.isRequired,
};

export default VehicleDetails;
